<template>
  <w-settings-content-template>
    <div slot="content">
      <el-row class="title-bar" type="flex" justify="space-between">
        <div class="vertical-center-box">
          <div class="title-h3 mr-r15">변수 목록</div>
          <el-radio-group class="ordinary setting mr-r4" v-model="variableTypeId" size="mini">
            <el-radio-button label="GLOBAL">GLOBAL</el-radio-button>
            <el-radio-button label="VARIABLE">VARIABLE</el-radio-button>
          </el-radio-group>
          <div class="setting-addel" v-show="variableTypeId === 'VARIABLE'">
            <el-button size="mini" @click="onAddButtonClick"><i class="fas fa-plus"></i></el-button>
            <el-button size="mini" :disabled="!isAbleDeleted" @click="onDeleteButtonClick"><i class="fas fa-minus"></i></el-button>
          </div>
        </div>
        <div class="vertical-center-box" align="right">
          <w-header-search-button @click="showSearchTool = !showSearchTool"></w-header-search-button>
        </div>
      </el-row>
      <div v-show="variableTypeId === 'GLOBAL'">
        <el-table-wrapper ref="globalTable" :data="tableData" :columns="tableColumns.GLOBAL" :show-search-tool="showSearchTool" :show-custom-header="true"
        remote pagination :total="total" @condition-change="loadData" @pagination-change="handlerPaginationChange" @total-change="handlerPaginationChange" />
      </div>
      <div v-show="variableTypeId === 'VARIABLE'">
        <el-table-wrapper ref="variableTable" :data="tableData" :columns="tableColumns.VARIABLE" :show-search-tool="showSearchTool" :show-custom-header="true"
        row-class-name="c-dblclick" remote pagination :total="total" @row-dblclick="onRowDblClick" @selection-change="onSelectionChange" @condition-change="loadData" />
      </div>
      <!-- 변수 추가/수정 팝업 -->
      <w-variable-setting-popup :selected-variable="selectedVariable" @re-load="loadData" ref="variableSettingPopup"></w-variable-setting-popup>
    </div>
  </w-settings-content-template>
</template>

<script>
import { isNil, concat } from 'lodash';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import msgUtil from '@/utils/message';
import { isTrue, booleanDefine } from '@/utils/boolean';
import { columnDefault, defaultPagination } from '@/constants/table';
import amsSettingsApi from '@/api/settings/server/ams-settings';
import WSettingsContentTemplate from '@/components/template/SettingsContentTemplate';
import WVariableSettingPopup from './popup/VariableSettingPopup';

const defaultFormatter = (row, column, value) => getValueAfterCheckEmpty(value);
const editableFlagFormatter = (row, column, value) => {
  if (isNil(value)) return EMPTY_TEXT;
  return isTrue(value) ? '가능' : '불가';
};
const passwordFormatter = (row, column, value) => {
  if (row.variableId === 'FTP_PASSWD') {
    let password = '';
    for (let i = 0; i < value.length; i += 1) {
      password = `${password}*`;
    }
    return password;
  }
  return getValueAfterCheckEmpty(value);
};
const defaultVariableColumns = [
  { prop: 'variableId', columnKey: 'variableId', label: '아이디', width: 300, searchable: true, sortable: true, remoteProp: 'variable_id' },
  { prop: 'variableName', columnKey: 'variableName', label: '이름', width: 300, searchable: true, sortable: true, remoteProp: 'variable_name', formatter: defaultFormatter },
  { prop: 'variableValueType', columnKey: 'variableValueType', label: '데이터 타입', width: 100, searchable: true, sortable: true, remoteProp: 'variable_value_type' },
  { prop: 'variableDefaultValue', columnKey: 'variableDefaultValue', label: '기본 값', formatter: passwordFormatter },
  { prop: 'variableDesc', columnKey: 'variableDesc', label: '설명', searchable: true, sortable: true, remoteProp: 'variable_desc', formatter: defaultFormatter },
  { prop: 'orderbyIndex', columnKey: 'orderbyIndex', label: '정렬 순서', width: 100, sortable: true, remoteProp: 'orderby_index', formatter: defaultFormatter },
];

export default {
  name: 'WVariableSetting',
  components: {
    WSettingsContentTemplate,
    WVariableSettingPopup,
  },
  data() {
    return {
      pagination: defaultPagination,
      columnDefault,
      total: 0,
      showSearchTool: false,
      refreshedTable: true,
      variableTypeId: 'GLOBAL',
      tableColumns: {
        GLOBAL: defaultVariableColumns,
        VARIABLE: concat(
          { type: 'selection' },
          defaultVariableColumns,
          { prop: 'editableFlag', columnKey: 'editableFlag', label: '수정', width: 70, sortable: true, remoteProp: 'editable_flag', formatter: editableFlagFormatter, filters: [{ text: '가능', value: booleanDefine.TRUE_Y }, { text: '불가', value: booleanDefine.FALSE_N }] },
        ),
      },
      tableData: [],
      checkedRows: [],
      rowSelectionAll: false,
      selectedVariable: null,
    };
  },
  computed: {
    isAbleDeleted() {
      let ableDeleteFlag = true;
      if (this.checkedRows.length > 0) {
        this.checkedRows.forEach((item) => {
          if (item.editableFlag === 'N') {
            ableDeleteFlag = false;
          }
        });
      } else {
        ableDeleteFlag = false;
      }
      return ableDeleteFlag;
    },
  },
  watch: {
    variableTypeId() {
      this.loadData();
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    /**
     * 변수 목록 로드
     * @author jeee12
     */
    loadData() {
      const typeId = this.variableTypeId;
      const tableParams = this.$refs[`${typeId.toLowerCase()}Table`].getParameter();

      amsSettingsApi.getVariableList({ typeId, ...tableParams }).then((response) => {
        if (response.data.success) {
          this.tableData = response.data.data;
          this.total = response.data.total;
        }
      });
    },
    /**
     * 추가 버튼 click
     * @author jeee12
     */
    onAddButtonClick() {
      this.selectedVariable = null;
      this.$nextTick(() => {
        this.$refs.variableSettingPopup.open();
      });
    },
    onRowDblClick(row) {
      this.selectedVariable = row;
      this.$nextTick(() => {
        this.$refs.variableSettingPopup.open();
      });
    },
    onSelectionChange(selection) {
      this.checkedRows = selection;
    },
    handlerPaginationChange(_pagination) {
      this.pagination = _pagination;
    },
    /**
     * 삭제 버튼 click
     * @author jeee12
     */
    onDeleteButtonClick() {
      // 기준정보가 아닌 row들만 삭제
      const editableRows = this.checkedRows.filter((data) => data.editableFlag === 'Y');
      this.$confirm(...msgUtil.getBasicConfirmData(msgUtil.action.DELETE)).then(() => {
        const target = '변수';
        const action = msgUtil.action.DELETE;
        amsSettingsApi.deleteVariableList(editableRows).then((response) => {
          if (response.data.success) {
            this.loadData();
          }
          if (editableRows.length !== this.checkedRows.length) {
            this.$message({ duration: 4500, ...msgUtil.getCustomResultMsgData('기준정보는 삭제할 수 없습니다.', response.data.success) });
          }
          if (editableRows.length > 0) {
            this.$message(msgUtil.getResultMsgData(action, target, response.data.success));
          }
        }).catch(() => {
          this.$message(msgUtil.getResultMsgData(action, target, false));
        });
      }).catch(() => {});
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/settings/server/AmsSettings/VariableSetting/index.vue